import Text from '@sats-group/fresh-fitness-ui-lib/react/text';
import WarningTime from '@sats-group/icons/24/warning-time';
import React, { useEffect, useRef } from 'react';
import { Temporal } from 'temporal-polyfill';

import { salesFlow } from 'root/experiments/pages/sales-flow/microcopy/sales-flow';

import type { CampaignCountdown as Props } from './campaign-countdown.types';

const maximumDays = 9999;
const secondsPerDay = 24 * 60 * 60;
const maximumCountdownSeconds =
  maximumDays * secondsPerDay + 23 * 60 * 60 + 59 * 60 + 59;
const campaignTimeZone = 'Europe/Oslo';

const getCampaignEndTime = (dateString: string) => {
  const [year, month, day] = dateString.slice(0, 10).split('-').map(Number);

  if (!year || !month || !day) {
    return;
  }

  try {
    return Temporal.ZonedDateTime.from({
      day,
      hour: 23,
      minute: 59,
      month,
      timeZone: campaignTimeZone,
      year,
    }).epochMilliseconds;
  } catch {
    return;
  }
};

const CampaignCountdown: React.FunctionComponent<Props> = ({ dateString }) => {
  const countdownRef = useRef<HTMLSpanElement>(null);
  const endTime = getCampaignEndTime(dateString);

  useEffect(() => {
    const countdown = countdownRef.current;
    if (!countdown || endTime === undefined) {
      return;
    }

    const remainingSeconds = Math.max(0, (endTime - Date.now()) / 1000);
    const countdownSeconds = Math.min(
      remainingSeconds,
      maximumCountdownSeconds,
    );
    const delay = -(maximumCountdownSeconds - countdownSeconds);

    countdown.style.animationDelay = `${delay}s, ${delay}s, ${delay}s, ${remainingSeconds}s`;
    countdown.classList.add('campaign-countdown--ready');
  }, [endTime]);

  if (endTime === undefined) {
    return null;
  }

  return (
    <span
      aria-label={`${salesFlow.campaignCountdownPrefix} ${dateString.slice(0, 10)} klokken 23:59`}
      className="campaign-countdown"
      ref={countdownRef}
      role="timer"
    >
      <span aria-hidden className="campaign-countdown__icon">
        <WarningTime />
      </span>
      <Text
        className="campaign-countdown__text"
        size={Text.sizes.small}
        theme={Text.themes.emphasis}
      >
        {salesFlow.campaignCountdownPrefix}
        <span className="campaign-countdown__days" />
        <span className="campaign-countdown__hours" />
        <span className="campaign-countdown__minutes" />
      </Text>
    </span>
  );
};

export default CampaignCountdown;
